import { useState } from 'react';
import { Shield, CheckCircle2, XCircle, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface ValidationResult {
  isValid: boolean;
  cardType: string;
  cardNumber: string;
}

// Luhn algorithm validation
const validateLuhn = (cardNumber: string): boolean => {
  const digits = cardNumber.replace(/\D/g, '').split('').reverse().map(Number);
  let sum = 0;
  
  for (let i = 0; i < digits.length; i++) {
    let digit = digits[i];
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  
  return sum % 10 === 0;
};

// Detect card type
const detectCardType = (cardNumber: string): string => {
  const cleanNumber = cardNumber.replace(/\D/g, '');
  
  // Visa
  if (/^4/.test(cleanNumber)) return 'Visa';
  
  // Mastercard
  if (/^5[1-5]/.test(cleanNumber) || /^2[2-7]/.test(cleanNumber)) return 'Mastercard';
  
  // American Express
  if (/^3[47]/.test(cleanNumber)) return 'American Express';
  
  // Discover
  if (/^6(?:011|5)/.test(cleanNumber)) return 'Discover';
  
  // JCB
  if (/^35(?:2[89]|[3-8])/.test(cleanNumber)) return 'JCB';
  
  // Diners Club
  if (/^3(?:0[0-5]|[68])/.test(cleanNumber)) return 'Diners Club';
  
  // Maestro
  if (/^(?:5[06789]|6)/.test(cleanNumber)) return 'Maestro';
  
  // UnionPay
  if (/^62/.test(cleanNumber)) return 'UnionPay';
  
  return 'Unknown';
};

const getCardColor = (cardType: string): string => {
  switch (cardType) {
    case 'Visa':
      return 'bg-blue-500/20 text-blue-500 border-blue-500/50';
    case 'Mastercard':
      return 'bg-red-500/20 text-red-500 border-red-500/50';
    case 'American Express':
      return 'bg-green-500/20 text-green-500 border-green-500/50';
    case 'Discover':
      return 'bg-orange-500/20 text-orange-500 border-orange-500/50';
    case 'JCB':
      return 'bg-purple-500/20 text-purple-500 border-purple-500/50';
    case 'Diners Club':
      return 'bg-cyan-500/20 text-cyan-500 border-cyan-500/50';
    case 'Maestro':
      return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50';
    case 'UnionPay':
      return 'bg-pink-500/20 text-pink-500 border-pink-500/50';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const CardValidator = () => {
  const [cardNumber, setCardNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ValidationResult | null>(null);

  const validateCard = () => {
    const cleanNumber = cardNumber.replace(/\D/g, '');
    
    if (!cleanNumber || cleanNumber.length < 13 || cleanNumber.length > 19) {
      toast.error('Please enter a valid card number (13-19 digits)');
      return;
    }

    setIsLoading(true);
    
    setTimeout(() => {
      const isValid = validateLuhn(cleanNumber);
      const cardType = detectCardType(cleanNumber);
      
      setResult({
        isValid,
        cardType,
        cardNumber: cleanNumber,
      });
      
      setIsLoading(false);
      
      if (isValid) {
        toast.success('Card number is valid!');
      } else {
        toast.error('Card number is invalid');
      }
    }, 300);
  };

  const formatCardNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
  };

  const handleCardInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 19);
    setCardNumber(formatCardNumber(value));
    setResult(null);
  };

  return (
    <Card className="glass border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Card Validator
        </CardTitle>
        <CardDescription>
          Validate card numbers using the Luhn algorithm
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="card-number">Card Number</Label>
          <Input
            id="card-number"
            placeholder="4242 4242 4242 4242"
            value={cardNumber}
            onChange={handleCardInput}
            className="font-mono text-lg"
          />
        </div>

        <Button onClick={validateCard} disabled={isLoading} className="w-full gap-2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Shield className="h-4 w-4" />
          )}
          Validate Card
        </Button>

        {result && (
          <div className="space-y-4 p-4 rounded-lg border bg-muted/30 animate-fade-in">
            <div className="flex items-center justify-center gap-3">
              {result.isValid ? (
                <div className="flex items-center gap-2 text-green-500">
                  <CheckCircle2 className="h-8 w-8" />
                  <span className="text-xl font-bold">Valid</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-destructive">
                  <XCircle className="h-8 w-8" />
                  <span className="text-xl font-bold">Invalid</span>
                </div>
              )}
            </div>

            <div className="grid gap-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                <span className="text-muted-foreground">Card Type</span>
                <Badge variant="outline" className={getCardColor(result.cardType)}>
                  <CreditCard className="h-3 w-3 mr-1" />
                  {result.cardType}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                <span className="text-muted-foreground">Card Number</span>
                <span className="font-mono">{formatCardNumber(result.cardNumber)}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                <span className="text-muted-foreground">Length</span>
                <span>{result.cardNumber.length} digits</span>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                <span className="text-muted-foreground">Luhn Check</span>
                <Badge variant={result.isValid ? 'default' : 'destructive'}>
                  {result.isValid ? 'Passed' : 'Failed'}
                </Badge>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CardValidator;
